import React, { useEffect, useMemo, useState } from 'react'
import { App, Button, Checkbox, Input, Segmented, Select, Switch } from 'antd'
import { describeAudience, type AnnouncementAudience } from '@muaa/shared'
import { brand } from '../theme/brand'
import { SectionCard } from '../theme/listkit'
import { Bell } from './icons'

/**
 * UrgentNoticeCard — write a message, choose who gets it, publish it to the top
 * of every running copy of the app within seconds.
 *
 * It sits on the Kill Switch page because it is the third lever on the same
 * singleton `app_config` row and belongs with the other incident comms — which
 * is why that tab reads System › App Controls › "Kill Switch & Notices".
 *
 * NOT RED. The page's own rule is that red/`danger` belongs to the kill switch
 * alone, so an admin can never mistake one lever for the other. A notice is
 * loud, not catastrophic: it gets gold.
 *
 * ─── WHY THE WRITES ARE SHAPED THE WAY THEY ARE ──────────────────────────────
 *
 * The app decides whether to re-show a dismissed notice by comparing an IDENTITY
 * string built from `level | announcement_published_at | message`
 * (mobile/src/features/notice/noticeIdentity.ts). Everything below follows from
 * that, and getting it wrong is silent — the admin publishes, nothing appears,
 * and no error is raised anywhere:
 *
 *  - PUBLISHING (off → on) writes the message, level, audience, `active` AND a
 *    fresh `announcement_published_at` in ONE update. One write, one audit entry,
 *    and the new timestamp guarantees the notice reaches even the people who
 *    dismissed a previous one with identical wording.
 *  - SAVING EDITS while live writes message/level/audience only. The text change
 *    ALREADY changes the identity, so it comes back to everyone by construction;
 *    re-stamping would be redundant.
 *  - "SHOW AGAIN" writes ONLY `announcement_published_at`. It is the lever for
 *    the one case the identity cannot detect on its own: the same words, still
 *    live, that people have read past and need to see once more.
 *  - UNPUBLISHING writes only `announcement_active: false`. The text stays so it
 *    can be reused, and no timestamp moves.
 *
 * ─── THE EMPTY-MESSAGE TRAP ──────────────────────────────────────────────────
 * The app requires `message.trim().length > 0` to render anything. An admin who
 * flipped this on with an empty box would publish silence and be told nothing was
 * wrong, so the switch is disabled until there is text and says why.
 *
 * ─── THE BOTH/NEITHER PLATFORM TRAP ──────────────────────────────────────────
 * `matchesAudience` drops an EMPTY platform array as "no constraint" — so
 * unchecking both boxes reaches everyone, exactly like checking both. That is the
 * right predicate (an audience should never silently reach nobody), but it is
 * surprising in a checkbox group, so the summary line says "everyone" in both
 * cases rather than leaving the admin to infer it.
 *
 * The audience summary itself comes from `describeAudience` in @muaa/shared — the
 * same file the app evaluates — so the sentence shown here cannot drift away from
 * who actually receives the notice.
 */

/** The announcement half of the app_config row. */
export interface AnnouncementConfig {
  announcement?: string | null
  announcement_active?: boolean | null
  announcement_level?: string | null
  announcement_audience?: unknown
  announcement_published_at?: string | null
}

export interface UrgentNoticeCardProps {
  config: AnnouncementConfig | null
  isLoading: boolean
  /**
   * The page's own writer — routes through its error handling and audit stamp.
   * Typed as a Partial of the announcement columns rather than
   * Record<string, unknown> so a typo here is a compile error rather than a write
   * that PostgREST rejects at runtime on a page that ships incident levers.
   */
  patchConfig: (values: Partial<AnnouncementConfig>) => Promise<void>
  refetch: () => Promise<void> | void
  /** The page's PostgREST-shape error reader (see KillSwitch.tsx). */
  describeWriteError: (e: unknown) => string
}

const MAX_NOTICE_LENGTH = 200

const PLACEHOLDER =
  'e.g. Homecoming check-in has moved to the Fellowship Hall — see you at 6pm.'

/** 'all' | 'in' | 'out' — the three states of the signed-in axis. */
type AudienceWho = 'all' | 'in' | 'out'

/** Read the stored jsonb back into the four control states the form edits. */
function formFromAudience(raw: unknown): {
  platforms: string[]
  who: AudienceWho
  minVersion: string
  maxVersion: string
} {
  const a =
    raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const platforms = Array.isArray(a.platforms)
    ? a.platforms.filter((p): p is string => p === 'ios' || p === 'android')
    : []
  const who: AudienceWho =
    typeof a.signedIn === 'boolean' ? (a.signedIn ? 'in' : 'out') : 'all'
  return {
    platforms,
    who,
    minVersion: typeof a.minAppVersion === 'string' ? a.minAppVersion : '',
    maxVersion: typeof a.maxAppVersion === 'string' ? a.maxAppVersion : '',
  }
}

/**
 * Build the jsonb to store. Omits every axis the admin left alone, so the common
 * case writes `{}` — the column default, and the value `matchesAudience` reads as
 * EVERYONE. A key with an "unconstrained" value would work too, but `{}` keeps
 * the row readable in psql and makes "is this targeted at all?" a one-glance
 * question for whoever is looking at it during an incident.
 */
export function audienceFromForm(form: {
  platforms: string[]
  who: AudienceWho
  minVersion: string
  maxVersion: string
}): AnnouncementAudience {
  const out: AnnouncementAudience = {}
  // 1 of 2 selected is a real constraint; 0 or 2 is not (see the header).
  if (form.platforms.length === 1) out.platforms = [...form.platforms]
  if (form.who === 'in') out.signedIn = true
  if (form.who === 'out') out.signedIn = false
  const min = form.minVersion.trim()
  const max = form.maxVersion.trim()
  if (min) out.minAppVersion = min
  if (max) out.maxAppVersion = max
  return out
}

/** Stable key for "has this audience actually changed?" — order-independent. */
function audienceKey(a: AnnouncementAudience): string {
  return JSON.stringify({
    platforms: a.platforms ? [...a.platforms].sort() : null,
    signedIn: a.signedIn ?? null,
    minAppVersion: a.minAppVersion ?? null,
    maxAppVersion: a.maxAppVersion ?? null,
  })
}

/** `1.2.0` or blank. Anything else is dropped by the matcher, so warn early. */
function versionLooksWrong(v: string): boolean {
  const t = v.trim()
  return t.length > 0 && !/^\d+(\.\d+)*$/.test(t)
}

export function UrgentNoticeCard({
  config,
  isLoading,
  patchConfig,
  refetch,
  describeWriteError,
}: UrgentNoticeCardProps) {
  const { message: toast, modal } = App.useApp()

  const serverActive = config?.announcement_active ?? false
  const serverMessage = config?.announcement ?? ''
  const serverLevel = config?.announcement_level === 'info' ? 'info' : 'urgent'
  const serverAudience = useMemo(
    () => formFromAudience(config?.announcement_audience),
    [config?.announcement_audience],
  )

  const [draft, setDraft] = useState('')
  const [level, setLevel] = useState<'info' | 'urgent'>('urgent')
  const [platforms, setPlatforms] = useState<string[]>([])
  const [who, setWho] = useState<AudienceWho>('all')
  const [minVersion, setMinVersion] = useState('')
  const [maxVersion, setMaxVersion] = useState('')

  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [restamping, setRestamping] = useState(false)
  // A toast is gone in seconds and a failed write leaves every control showing
  // the OLD server value, which reads as "nothing happened". The reason stays
  // pinned to the card until the next attempt — same treatment guest browsing
  // gets on this page, for the same reason.
  const [error, setError] = useState<string | null>(null)

  // Re-sync the whole form whenever the row (re)loads. Every control here is a
  // draft of one server value, so they all reset together or not at all.
  useEffect(() => {
    if (!config) return
    setDraft(serverMessage)
    setLevel(serverLevel)
    setPlatforms(serverAudience.platforms)
    setWho(serverAudience.who)
    setMinVersion(serverAudience.minVersion)
    setMaxVersion(serverAudience.maxVersion)
  }, [config, serverMessage, serverLevel, serverAudience])

  const draftAudience = useMemo(
    () => audienceFromForm({ platforms, who, minVersion, maxVersion }),
    [platforms, who, minVersion, maxVersion],
  )

  const trimmed = draft.trim()
  const hasText = trimmed.length > 0

  const dirty =
    config != null &&
    (trimmed !== serverMessage.trim() ||
      level !== serverLevel ||
      audienceKey(draftAudience) !==
        audienceKey(audienceFromForm(serverAudience)))

  const audienceSentence = describeAudience(draftAudience)
  const versionWarning =
    versionLooksWrong(minVersion) || versionLooksWrong(maxVersion)

  /** The message/level/audience triple — the "what to say and to whom" write. */
  function contentValues() {
    return {
      announcement: trimmed,
      announcement_level: level,
      announcement_audience: draftAudience,
    }
  }

  async function run(
    setBusy: (b: boolean) => void,
    values: Partial<AnnouncementConfig>,
    ok: string,
    verb: string,
  ) {
    setBusy(true)
    setError(null)
    try {
      await patchConfig(values)
      await refetch()
      toast.success(ok)
    } catch (e) {
      const msg = describeWriteError(e)
      setError(msg)
      toast.error(`Failed to ${verb}: ${msg}`)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Publishing is one write carrying the draft AND the switch AND a fresh
   * timestamp, so an admin who types a message and flips the switch never has to
   * discover that "Save" was a separate step. Unpublishing touches the switch
   * only — the words survive for reuse.
   */
  function requestToggle(next: boolean) {
    if (!next) {
      void run(
        setPublishing,
        { announcement_active: false },
        'Notice taken down. It disappears from the app within seconds.',
        'take the notice down',
      )
      return
    }
    modal.confirm({
      title: 'Publish this notice to every member?',
      okText: 'Yes, publish it',
      okType: 'primary',
      cancelText: 'Cancel',
      width: 540,
      content: (
        <div style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, color: brand.ink }}>
          <p style={{ marginTop: 4 }}>
            It appears as a band across the top of every screen — including the
            sign-in screen — reaching <strong>{audienceSentence.toLowerCase()}</strong> within
            seconds, with no app update.
          </p>
          <p
            style={{
              margin: '10px 0 0',
              padding: '10px 12px',
              borderRadius: 8,
              background: 'rgba(196,151,49,.10)',
              border: '1px solid rgba(196,151,49,.28)',
              fontStyle: 'italic',
            }}
          >
            {trimmed}
          </p>
          <p style={{ marginBottom: 0, marginTop: 10, color: brand.sub, fontSize: 12.5 }}>
            Anyone can read it, signed in or not — so keep operational detail out of it.
            Members can dismiss it; editing the wording brings it back.
          </p>
        </div>
      ),
      onOk: () =>
        run(
          setPublishing,
          {
            ...contentValues(),
            announcement_active: true,
            announcement_published_at: new Date().toISOString(),
          },
          'Notice published — it is on every connected device within seconds.',
          'publish the notice',
        ),
    })
  }

  function requestRestamp() {
    modal.confirm({
      title: 'Show this notice again to everyone?',
      okText: 'Yes, show it again',
      okType: 'primary',
      cancelText: 'Cancel',
      width: 500,
      content: (
        <div style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, color: brand.ink }}>
          Everyone who dismissed this notice will see it once more, with the wording
          unchanged. Use it when a message still matters and people have read past it.
        </div>
      ),
      onOk: () =>
        run(
          setRestamping,
          { announcement_published_at: new Date().toISOString() },
          'Notice re-shown to everyone who had dismissed it.',
          're-show the notice',
        ),
    })
  }

  const published = config?.announcement_published_at ?? null

  return (
    <SectionCard
      title="Urgent notice"
      sub="A short message across the top of every screen in the app — including the sign-in screen — live within seconds, with no app update."
      style={{ maxWidth: 720, marginBottom: 18 }}
    >
      <div data-testid="urgent-notice-card">
        {/* State line + the publish switch */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 20,
            flexWrap: 'wrap',
            paddingBottom: 16,
            borderBottom: `1px solid ${brand.cardLine}`,
          }}
        >
          <div style={{ display: 'flex', gap: 14, flex: 1, minWidth: 300 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                flexShrink: 0,
                background: serverActive ? 'rgba(196,151,49,.14)' : '#eef0f5',
                border: `1px solid ${serverActive ? 'rgba(196,151,49,.35)' : brand.line}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Bell size={20} color={serverActive ? '#8a6a1f' : brand.sub} />
            </div>
            <div>
              <div
                data-testid="urgent-notice-state"
                style={{
                  fontFamily: brand.fSans,
                  fontWeight: 700,
                  fontSize: 14,
                  color: brand.ink,
                  marginBottom: 5,
                }}
              >
                {isLoading
                  ? 'Loading…'
                  : serverActive
                  ? 'A notice is showing in the app right now'
                  : 'No notice is showing'}
              </div>
              <div
                data-testid="urgent-notice-live-audience"
                style={{
                  fontFamily: brand.fSans,
                  fontWeight: 500,
                  fontSize: 12.5,
                  color: brand.sub,
                  lineHeight: 1.6,
                }}
              >
                {serverActive ? (
                  <>
                    Reaching <strong>{describeAudience(config?.announcement_audience).toLowerCase()}</strong>
                    {published ? ` · published ${new Date(published).toLocaleString()}` : ''}.
                  </>
                ) : hasText ? (
                  'Your message is saved but not published. Turn this on to send it.'
                ) : (
                  'Write a message below, choose who should get it, then turn this on.'
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <span
              style={{
                fontFamily: brand.fSans,
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
                color: brand.sub,
              }}
            >
              {serverActive ? 'Live' : 'Off'}
            </span>
            <Switch
              data-testid="urgent-notice-toggle"
              checked={serverActive}
              loading={publishing}
              disabled={isLoading || (!serverActive && !hasText)}
              onChange={(checked) => requestToggle(checked)}
              style={serverActive ? { background: brand.gold } : undefined}
            />
          </div>
        </div>

        {/* The message */}
        <div style={{ marginTop: 16 }}>
          <FieldLabel>Message</FieldLabel>
          <Input.TextArea
            data-testid="urgent-notice-message"
            rows={3}
            value={draft}
            placeholder={PLACEHOLDER}
            maxLength={MAX_NOTICE_LENGTH}
            showCount
            onChange={(e) => setDraft(e.target.value)}
            disabled={isLoading || saving || publishing}
            style={{ fontFamily: brand.fSans }}
          />
          {!hasText && !isLoading && (
            <div data-testid="urgent-notice-empty-hint" style={hintStyle}>
              A notice with no words shows nothing in the app, so publishing stays
              switched off until you write one.
            </div>
          )}
        </div>

        {/* Level */}
        <div style={{ marginTop: 16 }}>
          <FieldLabel>Tone</FieldLabel>
          <Segmented
            data-testid="urgent-notice-level"
            value={level}
            onChange={(v) => setLevel(v as 'info' | 'urgent')}
            disabled={isLoading || saving || publishing}
            options={[
              { label: 'Information', value: 'info' },
              { label: 'Urgent', value: 'urgent' },
            ]}
          />
          <div style={hintStyle}>
            {level === 'urgent'
              ? 'Urgent draws a red rule down the side of the band and is announced immediately by VoiceOver and TalkBack.'
              : 'Information is the quieter treatment — the same band, in the usual navy and gold.'}
          </div>
        </div>

        {/* ── Targeting ───────────────────────────────────────────────────── */}
        <div
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: `1px solid ${brand.cardLine}`,
          }}
        >
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 13,
              color: brand.ink,
              marginBottom: 3,
            }}
          >
            Who sees it
          </div>
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 500,
              fontSize: 12,
              color: brand.sub,
              lineHeight: 1.6,
              marginBottom: 14,
            }}
          >
            Leave everything alone to reach everyone. Each limit you add narrows the
            audience further — they all have to be true.
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            <div style={{ minWidth: 210 }}>
              <FieldLabel>Phone type</FieldLabel>
              <Checkbox.Group
                data-testid="urgent-notice-platforms"
                value={platforms}
                onChange={(v) => setPlatforms(v as string[])}
                disabled={isLoading || saving || publishing}
                options={[
                  { label: 'iPhone / iPad', value: 'ios' },
                  { label: 'Android', value: 'android' },
                ]}
              />
              <div style={hintStyle}>
                {platforms.length === 1
                  ? `Only ${platforms[0] === 'ios' ? 'iPhone and iPad' : 'Android'} users will see it.`
                  : 'Both phone types — ticking both and ticking neither mean the same thing.'}
              </div>
            </div>

            <div style={{ minWidth: 240 }}>
              <FieldLabel>Sign-in state</FieldLabel>
              <Select
                data-testid="urgent-notice-who"
                value={who}
                onChange={(v) => setWho(v)}
                disabled={isLoading || saving || publishing}
                style={{ width: 240, fontFamily: brand.fSans }}
                options={[
                  { label: 'Everyone', value: 'all' },
                  { label: 'Signed-in members only', value: 'in' },
                  { label: 'Not signed in only', value: 'out' },
                ]}
              />
              <div style={hintStyle}>
                {who === 'out'
                  ? 'Only people looking at the sign-in screen or browsing as a guest.'
                  : who === 'in'
                  ? 'Only members with an active session on their phone.'
                  : 'Signed in or not — everybody who opens the app.'}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <FieldLabel>App version (optional)</FieldLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Input
                data-testid="urgent-notice-min-version"
                value={minVersion}
                onChange={(e) => setMinVersion(e.target.value)}
                placeholder="1.0.0"
                disabled={isLoading || saving || publishing}
                style={{ width: 120, fontFamily: brand.fSans }}
              />
              <span style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }}>
                or newer, up to
              </span>
              <Input
                data-testid="urgent-notice-max-version"
                value={maxVersion}
                onChange={(e) => setMaxVersion(e.target.value)}
                placeholder="1.4.2"
                disabled={isLoading || saving || publishing}
                style={{ width: 120, fontFamily: brand.fSans }}
              />
            </div>
            <div style={hintStyle}>
              Both ends are included, and either can be left blank. Use it to tell people
              on an old build to update — leave both empty for every version.
            </div>
            {versionWarning && (
              <div data-testid="urgent-notice-version-warning" style={warnStyle}>
                A version must look like <strong>1.4.2</strong>. Anything else is ignored by
                the app, so that limit would quietly do nothing.
              </div>
            )}
          </div>

          <div data-testid="urgent-notice-audience-summary" style={summaryStyle}>
            This notice goes to <strong>{audienceSentence}</strong>.
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 18,
            flexWrap: 'wrap',
          }}
        >
          <Button
            data-testid="urgent-notice-save"
            type="primary"
            loading={saving}
            disabled={isLoading || !dirty || !hasText}
            onClick={() =>
              void run(
                setSaving,
                contentValues(),
                serverActive
                  ? 'Notice updated — members see the new wording within seconds, including anyone who had dismissed it.'
                  : 'Notice saved. It is not published yet.',
                'save the notice',
              )
            }
          >
            {serverActive ? 'Update live notice' : 'Save draft'}
          </Button>
          {serverActive && (
            <Button
              data-testid="urgent-notice-restamp"
              loading={restamping}
              disabled={isLoading || dirty}
              onClick={requestRestamp}
            >
              Show again to everyone
            </Button>
          )}
          {dirty && (
            <span style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}>
              Unsaved changes
            </span>
          )}
        </div>

        {error && (
          <div data-testid="urgent-notice-error" style={{ ...warnStyle, color: '#a4452f' }}>
            Couldn&rsquo;t save this change: {error}. Everything above still shows what is
            actually live — try again.
          </div>
        )}

        {/* Preview */}
        <div style={{ marginTop: 20 }}>
          <FieldLabel>How it looks</FieldLabel>
          <NoticeBandPreview message={trimmed || PLACEHOLDER.replace(/^e\.g\. /, '')} level={level} />
        </div>
      </div>
    </SectionCard>
  )
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: brand.fSans,
        fontWeight: 700,
        fontSize: 10.5,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: brand.sub,
        marginBottom: 7,
      }}
    >
      {children}
    </div>
  )
}

const hintStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 500,
  fontSize: 12,
  color: brand.sub,
  lineHeight: 1.6,
  marginTop: 7,
  maxWidth: 560,
}

const warnStyle: React.CSSProperties = {
  ...hintStyle,
  fontWeight: 600,
  color: '#8a6a1f',
  marginTop: 9,
}

const summaryStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 500,
  fontSize: 13,
  color: brand.ink,
  lineHeight: 1.6,
  marginTop: 18,
  padding: '11px 14px',
  borderRadius: 10,
  background: 'rgba(196,151,49,.09)',
  border: '1px solid rgba(196,151,49,.26)',
}

/**
 * NoticeBandPreview — the top of a phone, 1:1 with
 * mobile/src/app/components/AnnouncementBanner.tsx. Cropped to the band rather
 * than drawing a whole handset because the band is the entire subject and a
 * full-height frame would push the controls above it off the screen.
 *
 * Colours are the mobile component's HARDCODED values, not the admin palette:
 * cream on navy900 (urgent) or navy800 (info), with `#FF8A8A` for the urgent
 * rule. The app hardcodes them because a member can pick a light colour scheme,
 * and reading foregrounds from the active scheme over a fixed navy surface
 * renders navy-on-navy (mobile/src/theme/tokens/colors.ts:132-149). This preview
 * has to show what members see, so it repeats the same fixed values.
 */
export function NoticeBandPreview({
  message,
  level,
}: {
  message: string
  level: 'info' | 'urgent'
}) {
  const urgent = level === 'urgent'
  return (
    <div
      data-testid="urgent-notice-preview"
      style={{
        width: 300,
        borderRadius: '22px 22px 10px 10px',
        background: '#0b0e16',
        padding: '9px 9px 0',
        boxShadow: '0 14px 34px rgba(6,18,43,.28)',
      }}
    >
      <div
        style={{
          borderRadius: '15px 15px 4px 4px',
          overflow: 'hidden',
          background: urgent ? '#06122B' : '#0a1c40',
        }}
      >
        {/* Status bar + Dynamic Island stand-in */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '9px 18px 7px',
            color: '#fff',
            fontFamily: brand.fSans,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          <span>9:41</span>
          <span
            style={{
              position: 'absolute',
              top: 5,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 74,
              height: 20,
              borderRadius: 11,
              background: '#000',
            }}
          />
          <span style={{ opacity: 0.85 }}>▮▮▮</span>
        </div>

        {/* The band itself */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '9px 16px',
            background: urgent ? '#06122B' : '#0a1c40',
            borderBottom: `1px solid ${urgent ? 'rgba(255,138,138,.45)' : '#c49731'}`,
          }}
        >
          {urgent && (
            <span
              style={{
                width: 3,
                alignSelf: 'stretch',
                minHeight: 17,
                borderRadius: 2,
                background: '#FF8A8A',
                marginRight: 10,
                flexShrink: 0,
              }}
            />
          )}
          <span
            style={{
              flex: 1,
              color: '#F6F1E4',
              fontFamily: brand.fSans,
              fontWeight: 600,
              fontSize: 11.5,
              lineHeight: 1.42,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {message}
          </span>
          <span
            style={{
              color: '#9aa4b5',
              fontFamily: brand.fSans,
              fontWeight: 600,
              fontSize: 13,
              paddingLeft: 10,
              flexShrink: 0,
            }}
          >
            ✕
          </span>
        </div>

        {/* A sliver of the app underneath, so the band reads as an overlay */}
        <div
          style={{
            height: 42,
            background: '#06122B',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            gap: 9,
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: brand.goldGrad,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: brand.fDisplay,
              fontWeight: 700,
              fontSize: 12.5,
              letterSpacing: '.09em',
              color: '#F6F1E4',
            }}
          >
            MUAA ALUMNI
          </span>
        </div>
      </div>
    </div>
  )
}
